import type { QuickAnswers } from "../components/bliss/BlissQuickStart";
import type { BlissRoomPlanContact } from "../config/blissRoomPlan";
import type { FunnelLeadSyncPayload } from "../services/funnelLeadSyncService";
import { contactPreferenceLabel } from "./contactPreferenceLabel";

export function quickStartLeadPayload({
  quoteRequestId,
  planId,
  contact,
  answers,
  completedThrough,
  submitted = false,
}: {
  quoteRequestId: string;
  planId: string;
  contact: BlissRoomPlanContact;
  answers: QuickAnswers;
  completedThrough: number;
  submitted?: boolean;
}): FunnelLeadSyncPayload {
  const custom = answers.productLine === "custom";
  const done = custom && submitted;
  const contactPreference = contactPreferenceLabel(answers.consultation?.contactPreference);
  const quickAnswers = custom
    ? {
        ...answers,
        bunks: [],
        configurationPreferences: [],
        customSubmitted: done,
      }
    : answers;
  return {
    quoteRequestId,
    notifyQuoteRequest: completedThrough >= 7 && (answers.consultation?.intent === 'designer' || (done && !answers.consultation?.intent)),
    stage: answers.consultation?.intent
      ? {
          designer: "designer_conversation_requested",
          deposit: "deposit_requested",
          room_review: "room_review_requested",
          design_only: "design_only_requested",
          monthly_plan: "monthly_prepayment_requested",
          save: "direction_saved",
        }[answers.consultation.intent]
      : done
        ? "custom_inquiry_submitted"
        : custom
          ? "custom_inquiry_in_progress"
          : completedThrough >= 5
            ? "preferences_completed"
            : completedThrough > 0
              ? "preferences_in_progress"
              : "funnel_started",
    contact: {
      name: contact.name,
      email: contact.email,
      phone: contact.phone,
      projectZip: contact.zip,
      ...(completedThrough >= 5 ? { timeline: answers.timing } : {}),
    },
    projectContext: {
      roomPlanId: planId,
      projectZip: contact.zip,
      quickAnswers,
      productLine: answers.productLine ?? null,
      customInquiry: custom
        ? {
            status: done ? "submitted" : "draft",
            idea: answers.customIdea?.trim() || "",
            inspirationLink: answers.inspirationLink?.trim() || "",
          }
        : null,
    },
    summary: answers.consultation
      ? [
          `Consultative journey: ${answers.consultation.intent ?? "exploring"}`,
          `Direction: ${answers.consultation.direction}`,
          contactPreference ? `Preferred contact: ${contactPreference}` : "",
          answers.consultation.intent === "monthly_plan"
            ? `Payment preference: ${answers.consultation.paymentFrequency === "biweekly" ? `${answers.consultation.planMonths === 12 ? 26 : 13} biweekly` : `${answers.consultation.planMonths} monthly`} interest-free automatic card payments, $${answers.consultation.upfrontAmount || 0} upfront; paid in full before installation. Requested installation month: ${answers.consultation.installationMonth || "To confirm"}. Schedule and terms require confirmation.`
            : "",
          `${answers.consultation.sleepers ?? "Undecided"} guests · ${answers.consultation.groups.join(", ")}`,
          `Access: ${answers.consultation.access || "To discuss"} · Legroom: ${answers.consultation.legroom || "To discuss"}`,
          answers.timing,
        ]
          .filter(Boolean)
          .join("\n")
      : custom
        ? [
            done
              ? "Custom Creation inquiry — ready for a Design Consultant conversation."
              : "Custom Creation preferences updated.",
            `${answers.sleepers === 6 ? "6+" : answers.sleepers} sleepers · ${answers.audience}`,
            answers.timing,
            answers.customIdea?.trim(),
            answers.inspirationLink?.trim(),
          ]
            .filter(Boolean)
            .join("\n")
        : completedThrough >= 5
          ? "Customer saved bunk preferences and timeline."
          : completedThrough > 0
            ? "Customer updated bunk preferences."
            : "Customer contact details saved.",
  };
}
